/**
 * @file This file contains the RoadManager class, responsible for generating
 * and managing a network of roads on the grid for visual effect.
 * @module RoadManager
 */

export class RoadManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {import('../models/Grid.js').Grid} dependencies.grid - The game grid instance.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./DebugManager.js').DebugManager} dependencies.debugManager - The debug manager instance.
     */
    constructor({ grid, ui, debugManager }) {
        this.grid = grid;
        this.ui = ui;
        this.debugManager = debugManager;
        /**
         * A set of "r,c" strings for road tile locations.
         * @type {Set<string>} - This will now store cell coordinates, not edges.
         */
        this.roadTiles = new Set();
    }

    /**
     * Initializes the manager, clearing old roads and generating new ones.
     */
    initManager() {
        this.roadTiles.clear();
        this.generateRoads();
        this.ui.renderRoads(this.roadTiles);
    }

    /**
     * Generates a dense road network covering approximately 90% of the grid.
     * It iterates through all possible horizontal and vertical road segments
     * and adds them based on a probability check.
     */
    generateRoads() {
        const roadProbability = 0.9; // 90% chance for any given road segment to exist.

        for (let r = this.grid.bufferSize; r < this.grid.size - this.grid.bufferSize; r++) {
            for (let c = this.grid.bufferSize; c < this.grid.size - this.grid.bufferSize; c++) {
                // Consider placing a horizontal road below cell (r, c)
                if (r < this.grid.size - this.grid.bufferSize - 1 && Math.random() < roadProbability) {
                    this.roadTiles.add(`h-${r},${c}`);
                }
                // Consider placing a vertical road to the right of cell (r, c)
                if (c < this.grid.size - this.grid.bufferSize - 1 && Math.random() < roadProbability) {
                    this.roadTiles.add(`v-${r},${c}`);
                }
            }
        }

        this.debugManager.log(`Generated a road with ${this.roadTiles.size} tiles.`);
    }

    getState() {
        return { roadTiles: Array.from(this.roadTiles) };
    }

    restoreState(state) {
        this.roadTiles = new Set(state.roadTiles || []);
        this.ui.renderRoads(this.roadTiles);
    }
}